import React, { useState, useRef } from 'react'
import { Trans } from '@lingui/macro'

import {
  TopicElementModelAsEditor,
  TopicModelAsMember,
  TopicModelAsEditor,
  TopicElementType,
  GoogleDriveFile
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { GooglePicker } from 'front-end/src/components/google/GooglePicker'
import { useTopicForEditQuery } from 'front-end/src/pages/topic-edit-page/TopicEditPage'
import { ButtonPrimary } from 'front-end/src/components'

import {
  usePatchGoogleDriveFileElementMutation,
  usePatchMicrosoftFileElementMutation
} from './EditEmbeddedFileElement.codegen'
import { EmbeddedFileElement } from '../../topic-elements/embedded-file-element/EmbeddedFileElement'
import { EditElementWrapper } from '../EditElementWrapper'
import useOnClickOutside from '../../../../app/hooks/useOnClickOutside'
import googleDrive from 'public/assets/img/google-drive.svg'
import oneDrive from 'public/assets/img/one-drive.svg'
import { MicrosoftPicker } from '../../../../components/microsoft/MicrosoftPicker'

import './EditEmbeddedFileElement.less'

export const EditEmbeddedFileElement = (props: {
  isMultiPage: boolean
  element: TopicElementModelAsEditor
  topic: TopicModelAsMember | TopicModelAsEditor
}) => {
  const { element, topic } = props
  const [patchGoogleDriveFileElement] = usePatchGoogleDriveFileElementMutation()
  const [patchMicrosoftFileElement] = usePatchMicrosoftFileElementMutation()
  const { refetch: refetchTopic } = useTopicForEditQuery()

  const [editing, setEditing] = useState(!element.data)
  const [embedLink, setEmbedLink] = useState('')

  const savePickedGoogleFile = async (selectedFile: GoogleDriveFile) => {
    if (selectedFile) {
      await patchGoogleDriveFileElement({
        variables: {
          topicId: topic.id,
          elementId: element.id,
          googleDriveFile: {
            description: selectedFile.description,
            embedUrl: selectedFile.embedUrl,
            iconUrl: selectedFile.iconUrl,
            id: selectedFile.id,
            lastEditedUtc: selectedFile.lastEditedUtc,
            mimeType: selectedFile.mimeType,
            name: selectedFile.name,
            type: selectedFile.type,
            url: selectedFile.url
          }
        }
      })

      await refetchTopic()

      setEditing(false)
    }
  }

  const savePickedMicrosoftFile = async (selectedFile, isPublic) => {
    if (selectedFile) {
      let embedUrl = selectedFile.webUrl.replace(
        /(action=).*?(&)/,
        '$1embedview$2'
      )

      if (isPublic) {
        embedUrl = `${selectedFile.permissions[0].link.webUrl}?action=embedview`
      }

      await patchMicrosoftFileElement({
        variables: {
          topicId: topic.id,
          elementId: element.id,
          microsoftFile: {
            embedUrl,
            id: selectedFile.id,
            mimeType: selectedFile.file.mimeType,
            name: selectedFile.name,
            url: selectedFile.permissions[0].link.webUrl
          }
        }
      })

      await refetchTopic()
      setEditing(false)
    }
  }

  const saveEmbedFormsLink = async (embedString) => {
    const match = embedString.match(/src\s*=\s*"(.+?)"/)
    if (match) {
      const embedUrl = match[1]
      if (embedUrl && element.type === TopicElementType.MICROSOFT_FILE) {
        await patchMicrosoftFileElement({
          variables: {
            topicId: topic.id,
            elementId: element.id,
            microsoftFile: {
              embedUrl
            }
          }
        })
      }

      await refetchTopic()
      setEditing(false)
    }
  }

  const googlePicker = (
    <GooglePicker
      onChange={(data) => {
        if (data.docs) {
          savePickedGoogleFile(data.docs[0])
        }
      }}
      multiselect={false}
      navHidden={true}
      authImmediate={false}
      mimeTypes={['application/vnd.google-apps.folder']}
      viewId={'DOCS'}
    >
      <div className="Drive__selector">
        <p className="info">
          <Trans>Select file to embed</Trans>
        </p>
        <img src={googleDrive} alt="Google Drive" width="80" height="80" />
      </div>
    </GooglePicker>
  )

  const microsoftPicker = (
    <MicrosoftPicker
      onChange={(data, isPublic) => {
        if (data.value) {
          savePickedMicrosoftFile(data.value[0], isPublic)
        }
      }}
    >
      <div className="Drive__selector">
        <p className="info">
          <Trans>Pick file from OneDrive or SharePoint</Trans>
        </p>
        <img src={oneDrive} alt="One drive" width="80" height="80" />
      </div>
    </MicrosoftPicker>
  )

  const wrapperRef = useRef(null)
  useOnClickOutside(wrapperRef, () => {
    if (element.data) {
      setEditing(false)
    }
  })

  const getViewElement = () => {
    const { data } = props.element
    if (!data) {
      return null
    }

    if (element.type === TopicElementType.GOOGLE_DRIVE_FILE) {
      return (
        <EmbeddedFileElement
          embedUrl={data.googleDriveFile.embedUrl}
          activateEditing={() => setEditing(true)}
        />
      )
    }

    if (element.type === TopicElementType.MICROSOFT_FILE) {
      return (
        <EmbeddedFileElement
          embedUrl={data.microsoftFile.embedUrl}
          activateEditing={() => setEditing(true)}
        />
      )
    }
  }

  return (
    <div ref={wrapperRef}>
      <EditElementWrapper {...props}>
        {!editing ? (
          getViewElement()
        ) : (
          <div className="Drive__search">
            {element.type === TopicElementType.MICROSOFT_FILE && (
              <>
                {microsoftPicker}{' '}
                <div className="Drive__input">
                  <p className="info">
                    <Trans>Microsoft Forms embed code</Trans>
                  </p>
                  <div className="link-edit__controls">
                    <input
                      id="link-input"
                      type="text"
                      onChange={(e) => setEmbedLink(e.target.value)}
                      placeholder="<iframe src= https://www.yourembedlink.com></iframe>"
                    />
                    <ButtonPrimary
                      disabled={!embedLink}
                      onClick={() => saveEmbedFormsLink(embedLink)}
                    >
                      <Trans>Save</Trans>
                    </ButtonPrimary>
                  </div>

                  <p className="info">
                    <a
                      href={
                        'https://support.microsoft.com/en-us/office/share-a-form-384371be-f1e7-4628-bcba-abd3d6123917'
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Trans>
                        How to get the embed code for Microsoft Forms?
                      </Trans>
                    </a>
                  </p>
                </div>
              </>
            )}
            {element.type === TopicElementType.GOOGLE_DRIVE_FILE &&
              googlePicker}
          </div>
        )}
      </EditElementWrapper>
    </div>
  )
}
